// src/auth/strategies/DevAuthManager.tsx
import React, { PropsWithChildren, useMemo, useState } from 'react';
import { generateToken } from '../utils/auth';
import { IdpInfo, useIdpInfo } from './IdpContext';
import { LoggedInUser, UserContext } from './UserContext';

// This component is guaranteed to use the hs-256-unsafe type, determined by the parent, AuthFactory
type UnsafeIdpInfo = Extract<IdpInfo, { algorithm: 'hs-256-unsafe' }>;
export const DevAuthManager: React.FC<PropsWithChildren> = ({ children }) => {
  const { idpInfo } = useIdpInfo() as { idpInfo: UnsafeIdpInfo };
  const [loggedInUser, setLoggedInUser] = useState<LoggedInUser>();

  const login = React.useCallback(
    async (userId?: string) => {
      if (!userId) throw new Error('User ID required');
      const token = await generateToken(userId, idpInfo.secret, idpInfo.audience, idpInfo.scope);
      setLoggedInUser({ userId, userAccessToken: token });
    },
    [idpInfo.audience, idpInfo.scope, idpInfo.secret],
  );
  const logout = React.useCallback(() => {
    setLoggedInUser(undefined);
    return Promise.resolve();
  }, []);

  const contextValue = useMemo(
    () => ({
      loggedInUser,
      isLoading: false,
      login,
      logout,
      isSelfSignedToken: () => true,
      signoutOnUnauthorized: () => {},
    }),
    [loggedInUser, login, logout],
  );

  return <UserContext.Provider value={contextValue}>{children}</UserContext.Provider>;
};
